/**
 * Loop-actor middleware tags (Client-safe) — OL-114.
 *
 * This file contains only the `Context.Tag` / `RpcMiddleware.Tag` declarations
 * for the MEMBER-facing outcome-engine surface. It has no server-side
 * dependencies (no `@fm/db/Database`, no Clerk registry) and can therefore be
 * imported by client packages, exactly as `admin.tags.ts` is — the server-side
 * implementation lives in `loop-actor.middleware.ts` and must never be imported
 * from `src/rpc.ts`.
 *
 * Vocabulary: a **loop** is a LIVING definition (`orcaloops.loops`), refined
 * between invocations; an **invocation** is one execution of it
 * (`orcaloops.outcomes`). The engine's own spellings are untouched — the
 * invocation group's rpcs are still named `outcomes.*`.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import type { Person } from "@fm/db/frontend";
import { Context, Schema } from "effect";

import { Forbidden, Unauthorized } from "./errors.ts";

// ============================================================================
// Context Tags
// ============================================================================

/**
 * The actor behind a member-facing loop RPC, however they authenticated.
 *
 * It is deliberately a SEPARATE tag from `CurrentActor` rather than a widening
 * of it: `CurrentActor` is shared by `AuthMiddleware` and `AdminMiddleware`, and
 * this surface needs two facts neither of those can supply. Because it carries
 * all three of `CurrentActor`'s fields it stays structurally assignable to
 * `typeof CurrentActor.Service`, so `requireCustomerMember`
 * (`admin/orcaloops-scope.ts`) takes it unchanged.
 *
 * @since 1.0.0
 */
export class LoopActor extends Context.Tag("LoopActor")<
  LoopActor,
  {
    readonly clerkId: string;
    readonly sessionId: string;
    readonly person: typeof Person.Type;
    /**
     * Which credential authenticated this call.
     *
     * **This field has exactly THREE GATES in the whole project, and all are
     * named here**: the mint/revoke rules on the `accessTokens.*` group (a
     * token-authed actor may not mint another token), the agent budget
     * ceiling in the invocation-create handler, and the Clerk-only rule on the
     * `oauthConsent.*` group (`authenticated/oauth-consent.router.ts`: a
     * token-authed actor may not approve an OAuth sign-in and so mint itself a
     * second credential). A fourth gate is a design smell, not a feature — the
     * entire point of one middleware over two auth paths is that handlers stay
     * auth-mechanism-blind.
     *
     * The one other reader, `draftWriterVia`
     * (`authenticated/loop-drafts.router.ts`), is NOT a gate: it turns the
     * credential into the `via` LABEL a draft row records beside "last written
     * by" (`ocl_` token → `mcp`, Clerk session → `web`), and decides nothing
     * about what the caller may do.
     *
     * In particular `resolveActorCustomer` is deliberately NOT one of those
     * readers: it branches on {@link customerId}, which is a fact about the
     * credential's SCOPE rather than about how the caller authenticated.
     */
    readonly authKind: "clerk" | "token";
    /** The `orcaloops.access_tokens` row id, when `authKind === "token"`. */
    readonly tokenId: string | null;
    /**
     * The token's PIN — `orcaloops.access_tokens.customer_id` of the row that
     * authenticated this call. `null` when the token is unpinned AND on every
     * Clerk-authed call. Read by `resolveActorCustomer` and by nothing else.
     *
     * The pin is carried ON THE ACTOR rather than re-read per call because the
     * access-tokens accessor exposes no `findById` — only `findByTokenHash` /
     * `listForPerson` — so a re-read would mean scanning the person's whole
     * token list on every rpc.
     */
    readonly customerId: string | null;
  }
>() {}

// ============================================================================
// Middleware Tags
// ============================================================================

/**
 * The one middleware behind every member-facing loop RPC.
 *
 * It accepts either a Clerk session JWT **or** an `ocl_`-prefixed access token
 * and yields the same {@link LoopActor} either way. It requires **no role** —
 * membership is the gate, and it is `resolveActorCustomer`
 * (`authenticated/loop-scope.ts`) that applies it.
 *
 * @since 1.0.0
 */
export class LoopActorMiddleware extends RpcMiddleware.Tag<LoopActorMiddleware>()(
  "LoopActorMiddleware",
  {
    provides: LoopActor,
    failure: Schema.Union(Unauthorized, Forbidden),
  },
) {}
